import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { CheckOutlined, InboxOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { useNotifications } from '@hooks/useNotifications';
import { useCollaborationInviteAction } from '@hooks/projects/useCollaborationInviteAction';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    ListItem,
    ListItemTitle,
    ListItemBody,
    ListItemMeta,
    ListItemContentRow,
    UnreadDot,
    EmptyState,
    DetailPanelLoading,
    ListCardWithItemPadding,
    RolesSectionSubtitle,
} from './style';

const FILTER_UNREAD = 'unread';
const FILTER_READ = 'read';

const formatRelative = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return 'Just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
    if (diffMs < 604800000) return `${Math.floor(diffMs / 86400000)}d ago`;
    return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
};

const Notifications = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location.pathname || '';
    const filter = useMemo(() => {
        if (pathname.endsWith('/read')) return FILTER_READ;
        return FILTER_UNREAD;
    }, [pathname]);

    const { items, isLoading, markRead, markAllRead, markAllReadPending, unreadCount, formatDate } = useNotifications({
        limit: 50,
        page: 1,
    });
    const { accept, reject, acceptPending, rejectPending } = useCollaborationInviteAction();

    const filteredItems = useMemo(() => {
        if (filter === FILTER_UNREAD) return items.filter((n) => !n.read);
        if (filter === FILTER_READ) return items.filter((n) => n.read);
        return items;
    }, [items, filter]);

    const showMarkAllRead = unreadCount > 0 && filter === FILTER_UNREAD;

    const getProjectTabForType = (type) => {
        if (!type) return 'details';
        const t = type.toLowerCase();
        if (t.includes('memo')) return 'submission-memos';
        if (t.includes('milestone')) return 'milestones';
        if (t.includes('blocker')) return 'blockers';
        if (t.includes('bug') || t.includes('qa')) return 'qa';
        if (t.includes('payment')) return 'payments';
        if (t.includes('feedback')) return 'feedback';
        if (t.includes('discussion')) return 'discussion';
        if (t.includes('collaboration')) return 'collaboration';
        if (t.includes('sprint') || t.includes('task')) return 'sprints';
        return 'details';
    };

    const handleNotificationClick = (n) => {
        if (!n.read) {
            markRead(n.id);
        }

        const isTenant = pathname.startsWith('/tenant');
        const meta = n.metadata ?? {};
        const projectId = meta.projectId ?? meta.project_id;
        const chatId = meta.chatId ?? meta.chat_id;
        const leadId = meta.leadId ?? meta.lead_id;
        const openMarketLeadId = meta.openMarketLeadId ?? meta.openMarketLead_id;

        if (projectId) {
            const tab = getProjectTabForType(n.type);
            navigate(isTenant ? `/tenant/projects/${projectId}?tab=${tab}` : `/projects/${projectId}?tab=${tab}`);
        } else if (chatId) {
            navigate(isTenant ? `/tenant/chat/${chatId}` : `/chat/${chatId}`);
        } else if (leadId) {
            navigate(isTenant ? `/tenant/sales/leads` : `/sales/leads`);
        } else if (openMarketLeadId || n.type?.startsWith('open_market')) {
            navigate(isTenant ? `/tenant/open-market/overview` : `/open-market/overview`);
        } else if (n.type?.startsWith('user_removal') || n.type === 'removal_request') {
            navigate(isTenant ? `/tenant/hr/removal-requests` : `/hr/removal-requests`);
        }
    };

    return (
        <ListBodyLayout
            title="Notifications"
            description="Manage your alerts and activity"
            actions={
                showMarkAllRead && (
                    <Button
                        $primary={false}
                        $size="14px"
                        onClick={() => markAllRead()}
                        loading={markAllReadPending}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                    >
                        {!markAllReadPending && <CheckOutlined />}
                        Mark all as read
                    </Button>
                )
            }
        >
            {/* List */}
            {isLoading ? (
                <DetailPanelLoading>
                    <Spin size="large" />
                </DetailPanelLoading>
            ) : filteredItems.length === 0 ? (
                <ListCardWithItemPadding
                    style={{
                        padding: '56px 28px',
                        textAlign: 'center',
                        borderRadius: 16,
                    }}
                >
                    <div
                        style={{
                            marginBottom: 16,
                            color: 'var(--ant-color-text-tertiary)',
                            fontSize: 48,
                        }}
                    >
                        <InboxOutlined />
                    </div>
                    <EmptyState style={{ padding: 0, fontSize: 16 }}>
                        {filter === FILTER_UNREAD && 'No unread notifications'}
                        {filter === FILTER_READ && 'No read notifications'}
                    </EmptyState>
                    <RolesSectionSubtitle style={{ marginTop: 8 }}>
                        {filter === FILTER_UNREAD
                            ? 'When you get alerts, they’ll show up here.'
                            : 'Try switching to Unread in the sidebar to see more.'}
                    </RolesSectionSubtitle>
                </ListCardWithItemPadding>
            ) : (
                <ListCardWithItemPadding style={{ borderRadius: 16, overflow: 'hidden' }}>
                    {filteredItems.map((n) => {
                        const title = n.title ?? n.message ?? 'Notification';
                        const body = n.body ?? n.message;
                        const showBody = body && body !== title;
                        const isCollaborationInvited = n.type === 'collaboration_invited';
                        const meta = n.metadata ?? {};
                        const projectId = meta.projectId ?? meta.project_id;
                        const collaborationId = meta.collaborationId ?? meta.collaboration_id;
                        const canRespond = isCollaborationInvited && projectId;

                        return (
                            <ListItem
                                key={n.id}
                                type="button"
                                $read={!!n.read}
                                onClick={() => handleNotificationClick(n)}
                            >
                                <ListItemContentRow>
                                    {!n.read && <UnreadDot />}
                                    {n.read && <span style={{ width: 8, flexShrink: 0, marginTop: 6 }} />}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <ListItemTitle>{title}</ListItemTitle>
                                        {showBody && <ListItemBody>{body}</ListItemBody>}
                                        <ListItemMeta title={formatDate(n.createdAt ?? n.created_at)}>
                                            {formatRelative(n.createdAt ?? n.created_at)}
                                        </ListItemMeta>
                                        {canRespond && (
                                            <Container
                                                $display="flex"
                                                $gap="8px"
                                                $marginTop="12px"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Button
                                                    type="button"
                                                    $primary
                                                    $blackOnLight
                                                    $size="12px"
                                                    $padding="6px 12px"
                                                    onClick={() => accept({ projectId, collaborationId })}
                                                    loading={acceptPending}
                                                    disabled={rejectPending}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    type="button"
                                                    $primary={false}
                                                    $size="12px"
                                                    $padding="6px 12px"
                                                    onClick={() => reject({ projectId, collaborationId })}
                                                    loading={rejectPending}
                                                    disabled={acceptPending}
                                                    style={{ color: 'var(--ant-color-error)' }}
                                                >
                                                    Reject
                                                </Button>
                                            </Container>
                                        )}
                                    </div>
                                </ListItemContentRow>
                            </ListItem>
                        );
                    })}
                </ListCardWithItemPadding>
            )}
        </ListBodyLayout>
    );
};

export default Notifications;
